import React, { FC, Fragment, useEffect, useState } from 'react';
import { Forward, Gross } from 'ts/transfer/interfaces/TransferResultsResponse';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCircleArrowRight,
  faClock,
  faSuitcase,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { useSearchParams } from 'react-router-dom';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { imgsCar } from 'constants/generic';

interface TransferResultListProps {
  data: Forward[];
  onChoose: (data: Forward) => void;
  choosenArrivalId?: string;
  choosenReturnId?: string;
  currency: keyof Gross;
}

interface Vehicle {
  desc: string;
  maxLuggage: number;
  maxPassengers: number;
  duration: number;
  price: number;
  pricePerPerson: number;
  personCount: number;
  currencySign: string;
  index: number;
  code: number;
}

const TransferResultList: FC<TransferResultListProps> = (props) => {
  const [t] = useTranslation();
  const [data, setData] = useState<Vehicle[]>([]);
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const colorSuccess = getComputedStyle(document.body).getPropertyValue('--color-success');
  
  const [urlParams] = useSearchParams();
  const [choosen, setChoosen] = useState<number>();

  useEffect(() => {
    const choosen = props.data.findIndex(
      (item) => item.priceKey === props.choosenArrivalId || item.priceKey === props.choosenReturnId
    );
    setChoosen(choosen);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  useEffect(() => {
    const oneWay = urlParams.get('searchType') === '0';
    let element;
    if (oneWay) {
      if (props.choosenArrivalId) {
        element = document.querySelector('.transfer-sum-data');
      }
    } else {
      if (props.choosenArrivalId && props.choosenReturnId) {
        element = document.querySelector('.transfer-sum-data');
      } else if (props.choosenArrivalId) {
        element = document.querySelector('.return-title');
      } else if (props.choosenReturnId) {
        element = document.querySelector('.transfer-results-data');
      }
    }

    if (element) {
      const y = element.getBoundingClientRect().top + window.scrollY - 10;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, [props.choosenArrivalId, props.choosenReturnId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const newData = props.data.map((item, index) => {
      let vehicle: Vehicle = {} as Vehicle;
      if (item.transfer?.transferVehicles.length) {
        const price = item.transfer?.transferVehicles[0]?.itemPrice.gross[props.currency] as number;
        vehicle.personCount = item.transfer.adult + item.transfer.cnn;
        vehicle.desc = item.transfer?.transferVehicles[0]?.vehicle.desc;
        vehicle.maxLuggage = item.transfer?.transferVehicles[0]?.vehicle.maxLuggage;
        vehicle.maxPassengers = item.transfer?.transferVehicles[0]?.vehicle.maxPassengers;
        vehicle.price = price;
        vehicle.pricePerPerson = price / vehicle.personCount;
        vehicle.duration = +item.transfer?.transferVehicles[0]?.transferDuration.tranTime;
        vehicle.currencySign = getCurrency(props.currency);
        vehicle.code = item.transfer?.transferVehicles[0]?.vehicle.transferVehicleTypeCode;
      }
      vehicle.index = index;
      return vehicle;
    });
    setData(newData);
  }, []); //  eslint-disable-line react-hooks/exhaustive-deps

  const choose = (index: number) => {
    props.onChoose(props.data[index]);
    setChoosen(index);
  };

  return (
    <Fragment>
      {data.map((item, index) => (
        <div className="default-shadow transfer-card" key={index}>
          <div className="transfer-card-img">
            {!!imgsCar?.length && (
              <img src={item.code <= imgsCar.length ? imgsCar[item.code] : imgsCar[0]} alt="car" />
            )}
          </div>
          <div className="transfer-card-data">
            <div className="card-title">{item.desc}</div>
            <div className="card-data">
              <div className="card-data-item">
                <FontAwesomeIcon icon={faUser} />
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('transfer.max_people', {
                      count: item.maxPassengers,
                    }),
                  }}
                ></div>
              </div>
              <div className="card-data-item">
                <FontAwesomeIcon icon={faSuitcase} />
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('transfer.max_suitcases', {
                      count: item.maxLuggage,
                    }),
                  }}
                ></div>
              </div>
              <div className="card-data-item">
                <FontAwesomeIcon icon={faClock} />
                <div
                  dangerouslySetInnerHTML={{
                    __html: t('transfer.est_time', {
                      h:
                        item.duration / 60 >= 1
                          ? Math.floor(item.duration / 60) + t('hours_sm')
                          : '',
                      min: Math.ceil(item.duration % 60) + t('minutes_sm'),
                    }),
                  }}
                ></div>
              </div>
            </div>
          </div>
          <div className="transfer-card-info text-center">
            {item.personCount > 1 && (
              <div className="mb-10">
                <div className="text-sm mb-5">{t('flight.avarage_per_passenger')}:</div>
                <div className="text-bold">
                  {formatPriceCurrency(item.pricePerPerson, item.currencySign)}
                </div>
              </div>
            )}

            <div className="price-total">
              <div className="text-sm mb-5">{t('flight.total_price')}:</div>
              <div className="text-bold mr-5 mb-10">
                {formatPriceCurrency(item.price, item.currencySign)}
              </div>

              {choosen === index ? (
                <FontAwesomeIcon icon={faCheckCircle} color={colorSuccess} size="xl" />
              ) : (
                <FontAwesomeIcon
                  className="cur-poi"
                  icon={faCircleArrowRight}
                  color={colorContrast}
                  size="xl"
                  onClick={() => choose(index)}
                />
              )}
            </div>
          </div>
        </div>
      ))}
    </Fragment>
  );
};

export default withErrorBoundary(TransferResultList, 'TransferResultList');
